import Iconify from '@/components/media/iconify';
import { CreateDiscountRequest } from '@/services/billing/types/create-billing.type';
import { RHFCheckbox, RHFTextField, RHFNumberField } from '@components/forms/hook-form';
import { Box, Grid, Button, IconButton, Typography } from '@mui/material';
import { ShowIf } from '@utils/index';
import { useMemo, useState, useEffect } from 'react';
import { useWatch, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useBillingStore } from '../../store/billing.store';
import { groupUniqueItems } from '../../utils/list-data.utils';
import { getTotalMustBeBilled } from '../../utils/sales-order.utils';
import { useDecimalFormatter } from '@hooks/index';

export const BillingOrderDiscount = () => {
  const { t, i18n } = useTranslation();
  const formatter = useDecimalFormatter(i18n.language);
  const { control, setValue } = useFormContext();

  const [amount, setAmount] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [description, setDescription] = useState('');

  const selectedMilestones = useBillingStore((state) => state.selectedMilestones);
  const addDiscount = useBillingStore((state) => state.addDiscount);
  const removeDiscount = useBillingStore((state) => state.removeDiscount);
  const clearDiscounts = useBillingStore((state) => state.clearDiscounts);

  const groupedUniqueItems = useMemo(() => {
    return groupUniqueItems(selectedMilestones);
  }, [selectedMilestones]);

  const watchedEnableDiscounts: boolean = useWatch({
    control,
    name: 'enableDiscounts',
  });

  const watchedDiscounts: CreateDiscountRequest[] = useWatch({
    control,
    name: 'discounts',
  });

  const subTotalAmountToBeBilled = useMemo(() => {
    return selectedMilestones.reduce((sum, so) => sum + so.total, 0);
  }, [selectedMilestones]);

  const totalDiscountAmount = useMemo(() => {
    return watchedDiscounts.reduce((sum, discount) => sum + discount.amount, 0);
  }, [watchedDiscounts]);

  const totalAmountToBeBilled = useMemo(() => {
    return subTotalAmountToBeBilled - totalDiscountAmount;
  }, [subTotalAmountToBeBilled, totalDiscountAmount]);

  const handleChangeAmount = (value: string) => {
    const parsedValue = parseFloat(value);
    if (!isNaN(parsedValue)) {
      setAmount(parsedValue);
    }
  };

  const handleChangeDescription = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {value} = e.target;
    setDescription(value);
  };

  const handleAddDiscount = () => {
    if (amount > totalAmountToBeBilled) {
      setErrorMessage(t('Discount amount cannot be greater than total amount to be billed'));
      return;
    }
    const newDiscount: CreateDiscountRequest = {
      amount,
      description,
    };
    const allDiscounts = [...watchedDiscounts, newDiscount];

    setValue('discounts', allDiscounts, {
      shouldValidate: true,
    });
    addDiscount(newDiscount);
    setAmount(0);
    setDescription('');
    setErrorMessage('');
  };

  const handleRemoveDiscount = (index: number) => {
    const newDiscounts = [...watchedDiscounts];
    newDiscounts.splice(index, 1);
    setValue('discounts', newDiscounts);
    removeDiscount(index);
  };

  useEffect(() => {
    if (!watchedEnableDiscounts) {
      setValue('billingOrder.discounts', []);
      clearDiscounts();
    }
  }, [watchedEnableDiscounts]);

  useEffect(() => {
    let totalMustBeBilled = getTotalMustBeBilled(groupedUniqueItems);
    const distribuitedDiscount = totalDiscountAmount / totalMustBeBilled.length;
    totalMustBeBilled = totalMustBeBilled.map((value) => {
      return {
        itemId: value.itemId,
        total: value.total - distribuitedDiscount,
      };
    });
    setValue('totalMustBeBilled', totalMustBeBilled);
  }, [totalDiscountAmount]);

  return (
    <Box sx={{ px: 2, pb: 2 }}>
      <Grid container spacing={2} sx={{ pt: 2 }}>
        <Grid item xs={12}>
          <RHFCheckbox name="enableDiscounts" label={t('Add Discount')} />
        </Grid>

        <ShowIf condition={watchedEnableDiscounts}>
          <Grid item xs={12} md={6}>
            <RHFNumberField
              name="discountAmount"
              onValueChange={(values) => {
                handleChangeAmount(values.value);
              }}
              label={t('Amount')}
              thousandsGroupStyle="thousand"
              thousandSeparator
              decimalScale={2}
              prefix="$"
              allowNegative={false}
              inputProps={{ autoComplete: 'off' }}
              error={errorMessage.length > 0}
              helperText={
                <>
                  {errorMessage.length > 0 && (
                    <>
                      {errorMessage}
                      <br />
                    </>
                  )}

                  {t(`Enter the discount amount (Max: ${formatter.format(totalAmountToBeBilled)})`)}
                </>
              }
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <RHFTextField
              value={description}
              fullWidth
              onChange={handleChangeDescription}
              type="text"
              name="description"
              label={t('Description')}
              placeholder={t('Description of the discount')}
              multiline
              rows={2}
              helperText={t('Please provide a description for the discount')}
            />
          </Grid>

          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button variant="contained" color="error" size="small" onClick={() => {}}>
              {t('Cancel')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleAddDiscount}
              disabled={amount <= 0 || description.trim() === ''}
            >
              {t('Add Discount')}
            </Button>
          </Grid>
        </ShowIf>

        <ShowIf condition={watchedDiscounts.length > 0}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              {t('Applied Discounts')}
            </Typography>
            {watchedDiscounts.map((discount, index: number) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 2,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                  bgcolor: 'background.paper',
                }}
              >
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body1">
                    <strong>$ {formatter.format(discount.amount)}</strong> - {discount.description}
                  </Typography>
                </Box>
                <IconButton onClick={() => handleRemoveDiscount(index)} color="error" size="small">
                  <Iconify icon="mingcute:delete-2-line" />
                </IconButton>
              </Box>
            ))}
          </Grid>
        </ShowIf>
      </Grid>
    </Box>
  );
};
